import { useEffect } from 'react'

const guideUrl = 'https://getcatalogfix.com/guides/validate-woocommerce-product-csv'

function setMeta(name: string, content: string) {
  const selector = name.startsWith('og:') ? `meta[property="${name}"]` : `meta[name="${name}"]`
  const meta = document.querySelector<HTMLMetaElement>(selector)
  meta?.setAttribute('content', content)
}

export function GuidePage() {
  useEffect(() => {
    document.title = 'How to Validate a WooCommerce Product CSV Before Import | CatalogFix'
    setMeta('description', 'A practical pre-import checklist for WooCommerce product CSV files: required fields, duplicate SKUs, prices, stock, images, encoding, and safe test imports.')
    setMeta('og:type', 'article')
    setMeta('og:title', 'How to Validate a WooCommerce Product CSV Before Import')
    setMeta('og:description', 'Catch common product CSV problems before they reach WooCommerce with this practical, safety-first checklist.')
    setMeta('og:url', guideUrl)
    setMeta('twitter:title', 'How to Validate a WooCommerce Product CSV Before Import')
    setMeta('twitter:description', 'A practical, safety-first checklist for WooCommerce product CSV files.')

    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', guideUrl)

    const schema = document.createElement('script')
    schema.id = 'catalogfix-guide-schema'
    schema.type = 'application/ld+json'
    schema.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'How to Validate a WooCommerce Product CSV Before Import',
      description: 'A practical pre-import checklist for WooCommerce product CSV files.',
      datePublished: '2026-08-06',
      dateModified: '2026-08-06',
      author: { '@type': 'Organization', name: 'CatalogFix' },
      publisher: { '@type': 'Organization', name: 'CatalogFix' },
      mainEntityOfPage: guideUrl,
    })
    document.head.appendChild(schema)
    return () => schema.remove()
  }, [])

  return <div className="app-shell guide-shell">
    <header className="site-header">
      <a className="brand" href="/" aria-label="CatalogFix home"><span className="brand-mark" aria-hidden="true"><span>CF</span></span><span className="brand-name">CatalogFix</span></a>
      <nav className="site-nav" aria-label="Primary navigation">
        <a href="/">Home</a><a href="/demo">WooCommerce CSV Prep</a><a href="/merchant-audit">Google Feed Audit</a><a href="/#pricing">Pricing</a><a href="/privacy">Privacy</a>
        <a className="nav-demo" href="/demo">Prepare a CSV <span aria-hidden="true">↗</span></a>
      </nav>
    </header>

    <main>
      <article className="guide-article">
        <header className="guide-hero">
          <p className="eyebrow"><span aria-hidden="true" /> WooCommerce operations guide</p>
          <h1>How to validate a product CSV <em>before import.</em></h1>
          <p className="lede">A bad catalog import can create duplicates, broken prices, missing images, and hours of cleanup. This practical preflight catches the most common risks before WooCommerce touches your store.</p>
          <div className="guide-meta"><span>8-minute read</span><span>Updated August 6, 2026</span><span>Safety-first workflow</span></div>
        </header>

        <div className="guide-layout">
          <aside className="guide-toc" aria-label="On this page">
            <strong>On this page</strong>
            <a href="#quick-check">60-second check</a>
            <a href="#fields">Required fields</a>
            <a href="#failure-modes">Common failures</a>
            <a href="#safe-workflow">Safe workflow</a>
            <a href="#automatic-check">Run a local check</a>
          </aside>

          <div className="guide-content">
            <section id="quick-check">
              <p className="guide-number">01</p>
              <h2>The 60-second pre-import check</h2>
              <p>Before opening the WooCommerce importer, confirm these six things. If any answer is “no,” stop and fix the file first.</p>
              <ul className="check-list">
                <li><span>1</span><div><strong>The file is saved as UTF-8 CSV</strong><p>This prevents avoidable character and delimiter problems.</p></div></li>
                <li><span>2</span><div><strong>Every product has a name and a unique SKU</strong><p>WooCommerce uses the SKU or product ID when matching existing products.</p></div></li>
                <li><span>3</span><div><strong>Prices are numeric and use a consistent decimal format</strong><p>Remove currency symbols, mixed separators, and formulas that export as text.</p></div></li>
                <li><span>4</span><div><strong>Stock values are valid numbers</strong><p>Look for negative values, words in quantity columns, or blanks that have no deliberate meaning.</p></div></li>
                <li><span>5</span><div><strong>Image URLs are public and direct</strong><p>WooCommerce must be able to fetch external images without login pages or redirect scripts.</p></div></li>
                <li><span>6</span><div><strong>You have a backup and a small test batch</strong><p>Never make the full catalog your first import attempt.</p></div></li>
              </ul>
            </section>

            <section id="fields">
              <p className="guide-number">02</p>
              <h2>Start with the fields WooCommerce expects</h2>
              <p>WooCommerce’s built-in importer maps recognized column names automatically. Unrecognized columns are skipped unless you map them manually. At minimum, make sure each sellable row has a clear product name and a stable SKU.</p>
              <div className="guide-table-wrap"><table className="guide-table"><thead><tr><th>Field</th><th>Preflight rule</th><th>Typical problem</th></tr></thead><tbody>
                <tr><td>SKU</td><td>Unique, stable, no accidental spaces</td><td>Duplicate or silently changed product</td></tr>
                <tr><td>Name</td><td>Present on every product row</td><td>Row rejected or unusable listing</td></tr>
                <tr><td>Regular price</td><td>Numeric, same decimal convention</td><td>Wrong or missing price</td></tr>
                <tr><td>Stock</td><td>Whole number when quantity is managed</td><td>Incorrect availability</td></tr>
                <tr><td>Categories</td><td>Use <code>&gt;</code> for hierarchy, commas for multiple terms</td><td>Broken category structure</td></tr>
                <tr><td>Images</td><td>Public, directly accessible URLs</td><td>Missing product media</td></tr>
              </tbody></table></div>
            </section>

            <section id="failure-modes">
              <p className="guide-number">03</p>
              <h2>Five failures worth catching early</h2>
              <div className="failure-grid">
                <article><span>Duplicate SKU</span><p>Two supplier rows identify the same product, making the intended update ambiguous.</p></article>
                <article><span>Malformed price</span><p>Currency symbols, text, or mixed decimal separators turn a price into invalid data.</p></article>
                <article><span>Invalid stock</span><p>Negative, fractional, or non-numeric quantities can produce an inaccurate storefront.</p></article>
                <article><span>Missing identity</span><p>A blank name or SKU makes a row difficult to match, review, and safely update later.</p></article>
                <article><span>Unreachable image</span><p>A URL may look valid in a sheet but still require authentication or redirect to HTML.</p></article>
              </div>
            </section>

            <section id="safe-workflow">
              <p className="guide-number">04</p>
              <h2>A safer import workflow</h2>
              <ol className="workflow-list">
                <li><span>01</span><div><strong>Back up and use staging</strong><p>Create a current database backup. For material catalog changes, rehearse on a staging store first.</p></div></li>
                <li><span>02</span><div><strong>Validate outside the store</strong><p>Review duplicates, missing required values, prices, stock, categories, and image URLs before upload.</p></div></li>
                <li><span>03</span><div><strong>Import a small sample</strong><p>Use representative simple and variable products so mapping errors appear before the full run.</p></div></li>
                <li><span>04</span><div><strong>Review the storefront</strong><p>Check product pages, prices, images, categories, variations, and stock behavior—not only the importer summary.</p></div></li>
                <li><span>05</span><div><strong>Split large catalogs</strong><p>Smaller batches are easier to diagnose and recover if a supplier file contains unexpected data.</p></div></li>
              </ol>
              <div className="source-note"><strong>Primary source</strong><p>Import behavior and safety recommendations are based on the <a href="https://woocommerce.com/document/product-csv-importer-exporter/" target="_blank" rel="noreferrer">official WooCommerce Product CSV Importer and Exporter documentation ↗</a>.</p></div>
            </section>

            <section className="guide-cta" id="automatic-check">
              <div><p className="eyebrow"><span aria-hidden="true" /> Local catalog check</p><h2>See risky rows before WooCommerce does.</h2><p>CatalogFix checks CSV and Excel files in your browser. Run the synthetic demo or choose your own supplier file—catalog contents are not uploaded.</p></div>
              <a className="button primary" href="/demo">Open WooCommerce CSV Prep <span aria-hidden="true">↗</span></a>
            </section>

            <p className="guide-disclaimer">CatalogFix is a pre-import review tool, not a store backup or a guarantee that every WooCommerce import will succeed. Always verify changes on staging and keep a recoverable backup.</p>
          </div>
        </div>
      </article>
    </main>

    <footer><a className="brand footer-brand" href="/"><span className="brand-mark" aria-hidden="true"><span>CF</span></span><span className="brand-name">CatalogFix</span></a><span><a href="/woocommerce-plugin">WooCommerce Plugin</a> · <a href="/demo">CSV Prep</a> · <a href="/merchant-audit">Google Feed Audit</a> · <a href="/guides/validate-woocommerce-product-csv">Guide</a> · <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a> · <a href="mailto:catalogfix.team@gmail.com">Contact</a></span><span>Public beta · Not a store backup tool</span></footer>
  </div>
}
